<template>
    <!-- Header -->
    <part-header v-if="!isMobile && !props.embedded" class="mb-3"></part-header>

    <!-- Desktop view - Datatable -->
    <part-datatable 
        v-if="!isMobile || props.embedded"
        :request-id="props.requestId"
        :client-id="props.clientId"
        :tech-id="props.techId"
        :hide-filters="props.hideFilters"
        :embedded="props.embedded"
        :pm-only="props.pmOnly"
        :disable-actions="props.disableActions"
    />
    
    <!-- Mobile view - Card list -->
    <part-mobile 
        v-else
        :rows="rows"
        :total="total"
        :filter="filter"
        @refresh="loadData"
        @new-record="newRecord"
        @edit-record="editRecord"
        @set-filter="handleFilterUpdate"
    />
    
    <router-view name="modal"></router-view>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from "vue";
import { useRouter, useRoute, RouterView } from 'vue-router';
import Api, { ApiResponse } from "@/services/ApiService";
import type { PartModel } from "@/models/PartModel";
import PartDatatable from "@/components/Parts/PartDatatable.vue";
import PartMobile from "@/components/Parts/PartMobile.vue";
import PartHeader from "@/components/Parts/PartHeader.vue";

const router = useRouter();
const route = useRoute();

// Props
const props = defineProps({
    requestId: {
        type: [Number, String],
        default: null
    },
    clientId: {
        type: [Number, String],
        default: null
    },
    techId: {
        type: [Number, String],
        default: null
    },
    hideFilters: {
        type: Boolean,
        default: false
    },
    embedded: {
        type: Boolean,
        default: false
    },
    pmOnly: {
        type: Boolean,
        default: false
    },
    disableActions: {
        type: Boolean,
        default: false
    }
});

// ===== MOBILE DETECTION =====
const isMobile = ref(false);
let mediaQuery: MediaQueryList | null = null;

const handleMediaChange = (e: MediaQueryListEvent) => {
    isMobile.value = e.matches;
};

onMounted(() => {
    if (typeof window !== 'undefined') {
        mediaQuery = window.matchMedia('(max-width: 767px)');
        isMobile.value = mediaQuery.matches;
        mediaQuery.addEventListener('change', handleMediaChange);
        
        if (isMobile.value && !props.embedded) {
            loadData();
        }
    }
});

onUnmounted(() => {
    if (mediaQuery) {
        mediaQuery.removeEventListener('change', handleMediaChange);
    }
});

// ===== DATA STATE (for mobile view) =====
const total = ref(0);
const rows = ref<PartModel[]>([]);
const filter = ref<any>({});

// Load data from API
const loadData = async () => {
    try {
        const params: any = {
            Page: 0,
            Size: 100,
            IncludeRequest: true,
            IncludeContact: true
        };
        
        if (props.requestId) {
            params.RequestId = props.requestId;
        }
        
        const response: ApiResponse<PartModel[]> = await Api.loadParts(params);
        rows.value = response.Data || [];
        total.value = response.Meta?.Total || 0;
    } catch (error) {
        console.error('Failed to load data:', error);
        rows.value = [];
        total.value = 0;
    }
};

const newRecord = () => {
    if (props.embedded) {
        return;
    }
    router.push({ path: "/part/new" });
};

const editRecord = (id: number) => {
    if (props.embedded) {
        return;
    }
    router.push({ path: "/part/edit/" + id });
};

const handleFilterUpdate = (newFilter: any) => {
    filter.value = newFilter;
    loadData();
};

</script>
